import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { showToast } from './Toast';
import { useAnalytics } from '../hooks/useAnalytics';

const SubscriptionModal = ({
  isOpen, onClose, apiClient, user,
  highlightTier = null
}) => {
  const { subscriptionStatus, waitForSubscriptionActivation } = useAuth();
  const { requireParentalVerification } = useProfile();
  const { trackCheckoutStarted } = useAnalytics();
  const [billingPeriod, setBillingPeriod] = useState('annual');
  const [isProcessing, setIsProcessing] = useState(false);

  if (!isOpen) return null;

  const getCurrentPlanKey = () => {
    const tier = subscriptionStatus?.subscriptionType;
    const tierMap = { 'NONE': 'free', 'STARTER': 'starter', 'PLUS': 'plus', 'PRO': 'plus', 'FAMILY': 'family', 'UNLIMITED': 'unlimited', 'PREMIUM': 'plus', 'EDDY': 'plus', 'PHILIP': 'family', 'NIKHIL': 'unlimited' };
    return tierMap[tier] || 'free';
  };
  const currentPlanKey = getCurrentPlanKey();

  const planDescriptions = { free: 'Try Pixley', starter: 'For casual creators', plus: 'Best for most families', family: 'For multiple kids', unlimited: 'For frequent creators' };

  const plans = {
    free: { name: 'Free', monthlyPrice: 0, annualPrice: 0, starsPerWeek: 50, features: [{ text: 'Up to 1 min stories', on: true }, { text: '16:9 format only', on: true }, { text: 'English only', on: true }, { text: 'No downloads', on: false }], cta: 'Keep Free', disabled: true },
    starter: { name: 'Starter', monthlyPrice: 7.99, annualPrice: 5.99, starsPerWeek: 100, features: [{ text: 'Up to 1.5 min stories', on: true }, { text: '16:9 format only', on: true }, { text: 'English only', on: true }, { text: 'Watermarked downloads', on: true }], cta: 'Choose Starter' },
    plus: { name: 'Pro', monthlyPrice: 14.99, annualPrice: 11.99, starsPerWeek: 250, features: [{ text: 'Up to 3 min stories', on: true }, { text: '9:16 vertical + 16:9', on: true }, { text: 'All languages', on: true }, { text: 'Watermarked downloads', on: true }], cta: 'Start Pro', badge: 'MOST POPULAR' },
    family: { name: 'Family', monthlyPrice: 24.99, annualPrice: 18.99, starsPerWeek: 650, features: [{ text: 'Up to 5 min stories', on: true }, { text: 'All formats', on: true }, { text: 'All languages', on: true }, { text: 'Download videos', on: true }, { text: 'No watermark', on: true }, { text: 'Up to 5 child profiles', on: true }], cta: 'Choose Family' },
    unlimited: { name: 'Max', monthlyPrice: 49.99, annualPrice: 37.99, starsPerWeek: 1500, features: [{ text: 'Up to 5 min stories', on: true }, { text: 'All formats', on: true }, { text: 'All languages', on: true }, { text: 'Download videos', on: true }, { text: 'No watermark', on: true }, { text: 'Up to 5 child profiles', on: true }], cta: 'Choose Max', badge: 'BEST VALUE' },
  };

  const handleSubscribe = async (tier) => {
    const executeSubscribe = async () => {
      if (!user?.id || !user?.email) { showToast("Unable to process. Try logging out and back in.", "error"); return; }
      if (isProcessing) return;
      const hasActive = currentPlanKey !== 'free';

      if (hasActive && tier === 'free') {
        if (!window.confirm("Cancel your subscription? You'll keep access until end of billing period.")) return;
        try { setIsProcessing(true); const r = await apiClient.createStripePortalSession(user.id); if (r.status && r.data.portal_url) window.location.href = r.data.portal_url; else throw new Error(r.error); } catch (e) { showToast("Failed to open portal.", "error"); setIsProcessing(false); } return;
      }
      if (hasActive && tier !== 'free') {
        const tierH = ['free','starter','plus','family','unlimited'];
        const isUp = tierH.indexOf(tier) > tierH.indexOf(currentPlanKey);
        if (!window.confirm(isUp ? "Upgrade? You'll be charged a prorated amount." : "Downgrade? Takes effect immediately.")) return;
        try {
          setIsProcessing(true);
          const r = await apiClient.updateStripeSubscription(user.id, tier, billingPeriod);
          if (r.status) {
            showToast(`${isUp ? 'Upgraded' : 'Downgraded'}! Updating your plan...`, "success");
            // Poll until RevenueCat reflects the tier change so the reload
            // lands on a fresh cache instead of the old tier (30-min TTL).
            await waitForSubscriptionActivation({
              untilChangedFrom: subscriptionStatus?.subscriptionType,
              timeoutMs: 20000,
              intervalMs: 2500,
            });
            window.location.reload();
          } else throw new Error(r.error);
        } catch (e) { showToast("Failed to update subscription.", "error"); setIsProcessing(false); } return;
      }
      try { setIsProcessing(true); trackCheckoutStarted('subscription', tier); const r = await apiClient.createStripeCheckoutSession(user.id, user.email, tier, billingPeriod); if (r.status && r.data.checkout_url) window.location.href = r.data.checkout_url; else throw new Error(r.error); } catch (e) { showToast("Failed to start checkout.", "error"); setIsProcessing(false); }
    };
    const hasActive = currentPlanKey !== 'free';
    const action = hasActive && tier === 'free' ? 'cancel subscription' : !hasActive ? 'subscribe to premium' : 'change subscription';
    requireParentalVerification(action, executeSubscribe);
  };

  const renderPlanCard = (planKey, plan) => {
    const isCurrentPlan = planKey === currentPlanKey;
    const isHighlighted = plan.badge === 'MOST POPULAR';
    const price = billingPeriod === 'annual' ? plan.annualPrice : plan.monthlyPrice;
    const originalPrice = plan.monthlyPrice;
    const tierH = ['free','starter','plus','family','unlimited'];
    const isDown = tierH.indexOf(planKey) < tierH.indexOf(currentPlanKey);
    const isUp = tierH.indexOf(planKey) > tierH.indexOf(currentPlanKey);
    const hasActive = currentPlanKey !== 'free';

    let ctaText = plan.cta;
    let isDisabled = false;
    if (isCurrentPlan) { ctaText = 'Current Plan'; isDisabled = true; }
    else if (hasActive && isDown) { ctaText = planKey === 'free' ? 'Cancel' : 'Downgrade'; }
    else if (hasActive && isUp) { ctaText = 'Upgrade'; }

    return (
      <div key={planKey} style={{ position: "relative", display: "flex", flexDirection: "column" }}>
        {plan.badge && (
          <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", zIndex: 2 }}>
            <span style={{ padding: "5px 14px", borderRadius: 999, fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#FFF", background: plan.badge === 'MOST POPULAR' ? "#0065F8" : "#FF6B9A", boxShadow: "0px 3px 0px " + (plan.badge === 'MOST POPULAR' ? "#073B8E" : "#C94D73"), whiteSpace: "nowrap" }}>{plan.badge}</span>
          </div>
        )}

        <div style={{
          flex: 1, borderRadius: 20, padding: "28px 20px 20px", display: "flex", flexDirection: "column",
          background: "#FFFFFF",
          border: isHighlighted ? "2px solid #0065F8" : "2px solid rgba(11,26,51,0.08)",
          boxShadow: isHighlighted ? "0px 4px 0px rgba(0,101,248,0.15), 0px 12px 32px rgba(0,101,248,0.10)" : "0px 2px 0px rgba(11,26,51,0.04)",
          fontFamily: "'Inter', 'Fredoka', sans-serif",
        }}>
          {/* Plan name */}
          <h3 style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 22, fontWeight: 700, color: "#0065F8", textAlign: "center", margin: 0, textTransform: "uppercase" }}>{plan.name}</h3>
          <p style={{ fontSize: 12, color: "#334155", textAlign: "center", margin: "2px 0 12px" }}>{planDescriptions[planKey]}</p>

          {/* Price */}
          <div style={{ textAlign: "center", marginBottom: 16 }}>
            {planKey === 'free' ? (
              <>
                <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 42, fontWeight: 700, color: "#0B1A33", lineHeight: 1 }}>$0</div>
                <div style={{ fontSize: 12, color: "transparent", marginTop: 4, userSelect: "none" }}>&nbsp;<br/>&nbsp;</div>
              </>
            ) : (
              <>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 3 }}>
                  <span style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 42, fontWeight: 700, color: "#0B1A33", lineHeight: 1 }}>${price}</span>
                  <span style={{ fontSize: 14, color: "#334155", fontWeight: 500 }}>/mo</span>
                </div>
                <div style={{ fontSize: 12, color: "#334155", marginTop: 4 }}>
                  {billingPeriod === 'annual' ? (
                    <><span style={{ textDecoration: "line-through" }}>${originalPrice} monthly</span><br/>billed annually</>
                  ) : 'Billed monthly'}
                </div>
              </>
            )}
          </div>

          {/* Stars */}
          <div style={{ padding: "10px 12px", borderRadius: 14, background: "#EAF3FF", border: "1px solid rgba(0,101,248,0.1)", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 16 }}>
            <img src="/assets/pixley_stars.png" alt="Stars" style={{ width: 20, height: 20 }} onError={(e) => { e.target.style.display = 'none'; }} />
            <span style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 18, fontWeight: 700, color: "#0065F8" }}>{plan.starsPerWeek}</span>
            <span style={{ fontSize: 12, color: "#334155" }}>stars / <strong style={{ fontFamily: "'Fredoka', sans-serif", fontWeight: 700, color: "#0065F8" }}>week</strong></span>
          </div>

          {/* Features */}
          <ul style={{ listStyle: "none", padding: 0, margin: "0 0 20px", display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
            {plan.features.map((f, i) => (
              <li key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#0B1A33", fontWeight: 400 }}>
                {f.on ? (
                  <svg width="18" height="18" fill="#0065F8" viewBox="0 0 20 20" style={{ flexShrink: 0 }}><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                ) : (
                  <svg width="18" height="18" fill="#D7E3F3" viewBox="0 0 20 20" style={{ flexShrink: 0 }}><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z" clipRule="evenodd" /></svg>
                )}
                <span style={{ color: f.on ? "#0B1A33" : "#334155" }}>{f.text}</span>
              </li>
            ))}
          </ul>

          {/* CTA */}
          <button
            onClick={() => !isDisabled && handleSubscribe(planKey)}
            disabled={isDisabled || isProcessing}
            style={{
              width: "100%", padding: "12px 16px", borderRadius: 14, border: "none", fontSize: 15, fontWeight: 700, cursor: isDisabled ? "not-allowed" : "pointer",
              fontFamily: "'Fredoka', sans-serif",
              background: isDisabled ? "#EAF3FF" : isHighlighted ? "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)" : "#FFFFFF",
              color: isDisabled ? "#8CA0BC" : isHighlighted ? "#FFFFFF" : "#0065F8",
              boxShadow: isDisabled ? "none" : isHighlighted ? "0px 4px 0px #073B8E" : "0px 3px 0px rgba(11,26,51,0.06)",
              border: isDisabled || isHighlighted ? "none" : "2px solid #0065F8",
              transition: "transform 120ms cubic-bezier(.2,.8,.2,1), box-shadow 120ms cubic-bezier(.2,.8,.2,1)",
            }}
            onMouseDown={(e) => { if (!isDisabled) { e.currentTarget.style.transform = "translateY(3px)"; e.currentTarget.style.boxShadow = "0px 0px 0px transparent"; } }}
            onMouseUp={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = isHighlighted ? "0px 4px 0px #073B8E" : "0px 3px 0px rgba(11,26,51,0.06)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = isHighlighted ? "0px 4px 0px #073B8E" : "0px 3px 0px rgba(11,26,51,0.06)"; }}
          >
            {isProcessing ? 'Processing...' : ctaText}
          </button>
        </div>
      </div>
    );
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-2 sm:p-4" style={{ background: "rgba(11,26,51,0.42)", backdropFilter: "blur(8px)" }}>
      <div onClick={onClose} className="absolute inset-0" />

      <div style={{
        position: "relative", width: "100%", maxWidth: 1200, maxHeight: "92vh",
        background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
        borderRadius: 28,
        boxShadow: "0px 5px 0px rgba(11,26,51,0.10), 0px 24px 64px rgba(11,26,51,0.20)",
        overflow: "hidden", display: "flex", flexDirection: "column",
        fontFamily: "'Inter', 'Fredoka', sans-serif",
      }}>
        {/* Decorative background shapes */}
        <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
          <div style={{ position: "absolute", top: -60, left: -60, width: 200, height: 200, borderRadius: "50%", background: "rgba(0,101,248,0.04)" }} />
          <div style={{ position: "absolute", bottom: -80, right: -80, width: 260, height: 260, borderRadius: "50%", background: "rgba(0,202,255,0.05)" }} />
          <div style={{ position: "absolute", top: "40%", left: "10%", width: 120, height: 120, borderRadius: "50%", background: "rgba(127,198,255,0.04)" }} />
        </div>

        {/* Close */}
        <button onClick={onClose} style={{ position: "absolute", top: 16, right: 16, zIndex: 10, width: 44, height: 44, borderRadius: 14, background: "#FFFFFF", border: "2px solid rgba(11,26,51,0.08)", cursor: "pointer", boxShadow: "0px 3px 0px rgba(11,26,51,0.06)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, color: "#334155" }}>
          ✕
        </button>

        <div style={{ position: "relative", padding: "24px 24px 20px", overflowY: "auto" }}>
          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <img src="/assets/pixley-logo-full.png" alt="Pixley" style={{ height: 100, objectFit: "contain", margin: "0 auto 16px", display: "block" }} />
            <h2 style={{ fontFamily: "'Fredoka', sans-serif", fontSize: "clamp(22px, 3vw, 34px)", fontWeight: 700, color: "#0B1A33", margin: "0 0 6px", textTransform: "uppercase", letterSpacing: "-0.01em" }}>
              Choose the Right Plan for Your Family
            </h2>
            <p style={{ fontSize: 15, color: "#334155", margin: 0 }}>More stars, longer stories, and premium tools for every kind of creator.</p>
          </div>

          {/* Billing toggle */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginBottom: 28 }}>
            <span style={{ fontSize: 14, fontWeight: billingPeriod === 'monthly' ? 700 : 500, color: billingPeriod === 'monthly' ? "#0B1A33" : "#334155" }}>Monthly</span>
            <button
              onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'annual' : 'monthly')}
              style={{ position: "relative", width: 56, height: 30, borderRadius: 999, background: billingPeriod === 'annual' ? "#0065F8" : "#D7E3F3", border: "none", cursor: "pointer", transition: "background 200ms ease", boxShadow: "inset 0 2px 4px rgba(11,26,51,0.1)" }}
            >
              <div style={{ position: "absolute", top: 3, left: billingPeriod === 'annual' ? 29 : 3, width: 24, height: 24, borderRadius: "50%", background: "#FFF", boxShadow: "0px 2px 4px rgba(11,26,51,0.2)", transition: "left 200ms ease" }} />
            </button>
            <span style={{ fontSize: 14, fontWeight: billingPeriod === 'annual' ? 700 : 500, color: billingPeriod === 'annual' ? "#0B1A33" : "#334155" }}>Annual</span>
            <span style={{ padding: "5px 12px", borderRadius: 999, fontSize: 11, fontWeight: 800, background: "#0065F8", color: "#FFF", boxShadow: "0px 2px 0px #073B8E", letterSpacing: "0.04em" }}>SAVE 40%</span>
          </div>

          {/* Plans grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 14, marginBottom: 24 }} className="subscription-plans-grid">
            {renderPlanCard('free', plans.free)}
            {renderPlanCard('starter', plans.starter)}
            {renderPlanCard('plus', plans.plus)}
            {renderPlanCard('family', plans.family)}
            {renderPlanCard('unlimited', plans.unlimited)}
          </div>

          {/* Footer */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 24, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#334155" }}>
              <svg width="14" height="14" fill="none" stroke="#0065F8" viewBox="0 0 24 24" strokeWidth={2}><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
              <span>Secure checkout</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#334155" }}>
              <svg width="14" height="14" fill="none" stroke="#0065F8" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
              <span>Restore purchases</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#334155" }}>
              <svg width="14" height="14" fill="none" stroke="#0065F8" viewBox="0 0 24 24" strokeWidth={2}><circle cx="12" cy="12" r="10" /><path strokeLinecap="round" d="M12 16v-4m0-4h.01" /></svg>
              <span>Subscriptions renew automatically unless canceled.</span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .subscription-plans-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 500px) {
          .subscription-plans-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default SubscriptionModal;
